import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import BvBtn from 'volta/components/bv-btn';
import BvFormLabel from 'volta/components/bv-form-label';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvInlineError from 'volta/components/bv-inline-error';
import BvText from 'volta/components/bv-text';
import classNames from 'volta/helpers/class-names';
import guid from 'volta/helpers/guid';
import orElse from 'volta/helpers/or-else';

import type { SafeString } from '@ember/template';
import type { ComponentLike, ContentValue } from '@glint/template';
import type { BvBtnArgs } from 'volta/components/bv-btn';

export interface ILabelAction extends BvBtnArgs {
  icon?: string;
}

export interface BvLabelledSignature {
  Args: {
    action?: ILabelAction;
    class?: string;
    dataTestLabelled?: string;
    error?: TFieldError;
    fullWidth?: boolean;
    helpText?: string;
    id?: string;
    info?: ContentValue | ComponentLike;
    inline?: boolean;
    inlineForm?: boolean;
    isEdited?: boolean;
    label?: string | SafeString;
    labelHidden?: boolean;
    labelWrapperClass?: string;
    contentClass?: string;
    isRequired?: boolean;
    warning?: TFieldError;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class BvLabelledComponent extends Component<BvLabelledSignature> {
  get warningMessage() {
    const { warning } = this.args;
    if (Array.isArray(warning)) {
      return undefined;
    }
    return warning as ContentValue;
  }

  get warningMessages() {
    const { warning } = this.args;
    if (Array.isArray(warning)) {
      return warning as ContentValue[];
    }
    return undefined;
  }

  get errorMessage() {
    const { error } = this.args;
    if (Array.isArray(error) || typeof error === 'boolean') {
      return undefined;
    }
    return error as ContentValue;
  }

  get errorMessages() {
    const { error } = this.args;
    if (error && Array.isArray(error)) {
      return error as ContentValue[];
    }
    return undefined;
  }

  <template>
    {{#let (orElse @id (guid)) as |id|}}
      {{#let
        (concat id 'Error') (concat id 'Warning') (concat id 'HelpText')
        as |errorId warningId helpTextId|
      }}
        <div
          class={{classNames
            'bv-labelled'
            (if @labelHidden 'bv-labelled--hidden')
            (if @inline 'bv-labelled--inline')
            (if @inlineForm 'bv-labelled--inline-form')
            (if @fullWidth 'u-full-width')
            @class
          }}
          data-test-labelled={{orElse @dataTestLabelled true}}
          ...attributes
        >
          {{#if @label}}
            <BvInline
              @gap='2'
              @blockAlign='center'
              @align={{if @inline 'start' 'space-between'}}
              class={{classNames 'bv-labelled__label-wrapper' @labelWrapperClass}}
            >
              <BvFormLabel @labelFor={{id}} @hidden={{false}}>
                {{@label}}
                {{#if @isRequired}}
                  <BvText @color='critical' @text='*' />
                {{/if}}
                {{#if @isEdited}}
                  <BvIcon @glyph='pencil' @color='danger' @size='xs' />
                {{/if}}
              </BvFormLabel>
              {{#if @action}}
                <BvBtn
                  @plain={{true}}
                  @size='xs'
                  @color={{@action.color}}
                  @text={{orElse @action.content @action.text}}
                  @iconLeft={{@action.icon}}
                  @tooltip={{@action.tooltip}}
                  @disabled={{@action.disabled}}
                  @isLoading={{@action.isLoading}}
                  @accessibilityLabel={{@action.accessibilityLabel}}
                  @onClick={{optional @action.onClick}}
                  @testName={{@dataTestLabelled}}
                />
              {{/if}}
              {{! @glint-expect-error: ComponentLike != ContentValue }}
              {{@info}}
            </BvInline>
          {{/if}}
          <div class={{classNames 'bv-labelled__content' @contentClass}}>
            {{yield}}
            {{#if @error}}
              <div data-test-labelled-error class='bv-labelled__error' id={{errorId}}>
                {{#if this.errorMessages.length}}
                  <ul class='list-unstyled'>
                    {{#each this.errorMessages as |err|}}
                      {{#if err}}
                        <li>
                          <BvInlineError @message={{err}} @fieldID={{id}} />
                        </li>
                      {{/if}}
                    {{/each}}
                  </ul>
                {{else if this.errorMessage}}
                  <BvInlineError @message={{this.errorMessage}} @fieldID={{id}} />
                {{/if}}
              </div>
            {{/if}}
            {{#if @warning}}
              <div data-test-labelled-warning class='bv-labelled__warning' id={{warningId}}>
                {{#if this.warningMessages.length}}
                  <ul class='list-unstyled'>
                    {{#each this.warningMessages as |warn|}}
                      <li>
                        <BvInlineError @message={{warn}} @fieldID={{id}} @isWarning={{true}} />
                      </li>
                    {{/each}}
                  </ul>
                {{else if this.warningMessage}}
                  <BvInlineError
                    @message={{this.warningMessage}}
                    @fieldID={{id}}
                    @isWarning={{true}}
                  />
                {{/if}}
              </div>
            {{/if}}
            {{#if @helpText}}
              <div data-test-label-help-text id={{helpTextId}} class='bv-labelled__help-text'>
                {{@helpText}}
              </div>
            {{/if}}
          </div>
        </div>
      {{/let}}
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvLabelled: typeof BvLabelledComponent;
    'bv-labelled': typeof BvLabelledComponent;
  }
}
